import { useEffect, useState } from 'react';
import { apiClient, axiosClient } from '../../auth-lib';
import { Button, Modal, Input, Select, useToast } from '../../design-system';

// The daily expiry digest's standing arrangement: when it goes out, how much
// runway counts as "coming up", and the two people who answer for it.
//
// ⚠️ TWO NAMED PEOPLE, NOT A ROLE. A DSC that lapses blocks every e-filing it
// signs for, and "whoever has dsc.update" is nobody in particular — the failure
// mode of a duty owed by a group is that each member assumes another has it.
// One person renews, one checks the renewal landed, and both are told the same
// thing at the same time.
//
// ⚠️ THE CLASSES HERE ARE `dsc-form-*`, NOT `client-form` / `hint-text`. Those
// live in CreateEditClient.css and ClientDetail.css, neither of which this page
// loads — using them renders a correct but completely unstyled form, which is
// the kind of bug that only shows up on the screen nobody opened yet.

const fmtWhen = (iso) =>
  (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

function DscScheduleModal({ onClose }) {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiClient.get('/api/dscs/settings'),
      // The pick-list of people who can be named. Same endpoint the client form
      // uses for its partner/manager assignment.
      axiosClient.get('/users').catch(() => ({ data: [] })),
    ])
      .then(([s, u]) => {
        if (!alive) return;
        setForm(s.data);
        setStaff(u.data || []);
      })
      .catch(() => alive && setError('Could not load the schedule'));
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form) return;
    setError('');
    setSaving(true);
    try {
      const { data } = await apiClient.put('/api/dscs/settings', {
        enabled: form.enabled,
        time: form.time,
        leadDays: Number(form.leadDays),
        notifyWhenClear: form.notifyWhenClear,
        // '' → null so a seat can be cleared; the server refuses an id that
        // does not name somebody in this firm.
        ownerUserId: form.ownerUserId || null,
        reviewerUserId: form.reviewerUserId || null,
      });
      setForm(data);
      toast.success(data.enabled ? `Digest set for ${data.time} daily` : 'Digest switched off');
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the schedule');
    } finally {
      setSaving(false);
    }
  };

  const peopleOptions = [
    <option key="" value="">— Nobody —</option>,
    ...staff.map((u) => (
      <option key={u._id} value={u._id}>
        {u.name}{u.role?.name ? ` (${u.role.name})` : ''}
      </option>
    )),
  ];

  // ⚠️ The buttons go in `footer`, not inside the <form>. That is the prop this
  // Modal takes, and it renders them outside the form element — so `save` is
  // wired to onClick rather than to a submit, exactly as DscModal does it.
  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button variant="primary" onClick={save} loading={saving} disabled={!form || saving}>
        Save
      </Button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title="DSC expiry notifications" footer={footer} size="md">
      {!form ? (
        <p className="dsc-form-hint">Loading…</p>
      ) : (
        <form className="dsc-form" onSubmit={save}>
          {error && <div className="dsc-form-error" role="alert">{error}</div>}

          <label className="dsc-form-check">
            <input type="checkbox" checked={form.enabled} onChange={set('enabled')} />
            <span>Send a daily digest of expired and expiring DSCs</span>
          </label>

          <div className="dsc-form-row">
            <Input
              label="Send at"
              type="time"
              value={form.time}
              onChange={set('time')}
              helperText={`Daily, ${form.timezone}`}
            />
            <Input
              label="Warn this many days ahead"
              type="number"
              min="1"
              max="365"
              value={form.leadDays}
              onChange={set('leadDays')}
              helperText="30 matches the “Expiring ≤ 30 days” tile"
            />
          </div>

          <div className="dsc-form-row">
            <Select label="Preparer — renews them" value={form.ownerUserId || ''} onChange={set('ownerUserId')}>
              {peopleOptions}
            </Select>
            <Select label="Reviewer — checks the renewal" value={form.reviewerUserId || ''} onChange={set('reviewerUserId')}>
              {peopleOptions}
            </Select>
          </div>
          <p className="dsc-form-hint">
            These two are told, in the app and by email, and nobody else. Both get the
            same list at the same time, so a missed renewal is somebody&apos;s rather
            than nobody&apos;s.
          </p>

          {/* ⚠️ Off by default. A digest that arrives every morning reading
              "0 expired, 0 expiring" is one people stop opening — and the
              morning it finally carries something is the morning it gets
              archived unread with the rest. */}
          <label className="dsc-form-check">
            <input type="checkbox" checked={form.notifyWhenClear} onChange={set('notifyWhenClear')} />
            <span>Send it even when nothing is expiring (a daily all-clear)</span>
          </label>

          <p className="dsc-form-hint">
            Last sent: {form.lastRunOn ? `${form.lastRunOn} at ${fmtWhen(form.lastRunAt)}` : 'never'}
          </p>
        </form>
      )}
    </Modal>
  );
}

export default DscScheduleModal;
